import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../store/slices/affiliatesReducer";
import { useNavigate } from "react-router-dom";

const LandingPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.affiliate.auth);

  useEffect(() => {
    if (user) {
      navigate(user.affiliate_role === "admin" ? "/admin" : "/dashboard");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim() || !password.trim()) {
      setError("Please enter both email and password.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const credentials = {
        email: email,
        identification_code: password,
      };
      await dispatch(loginUser(credentials));
      navigate("/dashboard");
    } catch (err) {
      setError(err.message || "Login failed. Please check your credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Mobile Layout - Image as background with overlay */}
      <div
        className="laptop:hidden desktop:hidden relative min-h-screen bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url(/assets/images/menuwise.png)" }}
      >
        <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center p-6">
          <div className="w-full max-w-sm">
            <div className="w-16 h-16 mobile:w-20 mobile:h-20 tablet:w-24 tablet:h-24 laptop:w-24 laptop:h-24 desktop:w-24 desktop:h-24 bg-gradient-to-br from-secondary-green to-secondary-green/90 rounded-2xl flex items-center justify-center mx-auto mb-2 shadow-2xl">
              <img
                src="/assets/icons/menuwise_logo_white.png"
                alt="Menuwiz"
                className="w-8 h-8 mobile:w-10 mobile:h-10 tablet:w-12 tablet:h-12 laptop:w-12 laptop:h-12 desktop:w-12 desktop:h-12"
              />
            </div>
            <div className="text-center mb-8">
              <h1 className="text-xl mobile:text-2xl tablet:text-3xl laptop:text-3xl desktop:text-3xl font-bold text-primary-dark mb-3">
                Menuwiz
              </h1>
              <h1 className="text-3xl mobile:text-4xl tablet:text-5xl laptop:text-6xl desktop:text-6xl font-bold text-gray-900 mb-4 mobile:mb-6 tablet:mb-6 laptop:mb-6 desktop:mb-6 leading-tight">
                Hållbar mat för en{" "}
                <span className="text-orange-500">bättre framtid</span>
              </h1>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email Address"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {error && (
                <div className="text-red-600 text-sm text-center">{error}</div>
              )}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-6 bg-secondary-green hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                {isLoading ? "Signing in..." : "Sign in"}
              </button>
            </form>
            <div className="mt-8 text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <span className="text-sm text-gray-500">Powered by</span>
                <img
                  src="/assets/logo_low_res.png"
                  alt="Consupedia"
                  className="h-6 w-auto"
                />
              </div>
            </div>
            <div className="mt-8 text-center text-sm">
              <a
                href="mailto:info@consupedia.com"
                className="group  text-primary-dark hover:bg-gray-50 px-10 mobile:px-12 py-5 mobile:py-0 rounded-2xl text-sm mobile:text-base font-bold transition-all duration-300 hover:shadow-3xl hover:scale-105 transform inline-flex items-center"
              >
                <span className="flex items-center">
                  Kontakta Oss
                  <svg
                    className="w-6 h-6 ml-3 group-hover:translate-x-2 transition-transform duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Layout - Side by side */}
      <div className="hidden laptop:flex min-h-screen">
        <div
          className="w-[70%] bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url(/assets/images/menuwise.png)" }}
        ></div>
        <div className="w-[30%] bg-white flex items-center justify-center p-8">
          <div className="w-full max-w-sm">
            <div className="w-16 h-16 mobile:w-20 mobile:h-20 tablet:w-24 tablet:h-24 laptop:w-24 laptop:h-24 desktop:w-24 desktop:h-24 bg-gradient-to-br from-secondary-green to-secondary-green/90 rounded-2xl flex items-center justify-center mx-auto mb-2 shadow-2xl">
              <img
                src="/assets/icons/menuwise_logo_white.png"
                alt="Menuwiz"
                className="w-8 h-8 mobile:w-10 mobile:h-10 tablet:w-12 tablet:h-12 laptop:w-12 laptop:h-12 desktop:w-12 desktop:h-12"
              />
            </div>
            <div className="text-center mb-8">
              <h1 className="text-xl mobile:text-2xl tablet:text-3xl laptop:text-3xl desktop:text-3xl font-bold text-primary-dark mb-3">
                Menuwiz
              </h1>
              <h1 className="text-3xl mobile:text-4xl tablet:text-5xl laptop:text-6xl desktop:text-6xl font-bold text-gray-900 mb-4 mobile:mb-6 tablet:mb-6 laptop:mb-6 desktop:mb-6 leading-tight">
                Hållbar mat för en{" "}
                <span className="text-orange-500">bättre framtid</span>
              </h1>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email Address"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {error && (
                <div className="text-red-600 text-sm text-center">{error}</div>
              )}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-6 bg-secondary-green hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                {isLoading ? "Signing in..." : "Sign in"}
              </button>
            </form>
            <div className="mt-8 text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <span className="text-sm text-gray-500">Powered by</span>
                <img
                  src="/assets/logo_low_res.png"
                  alt="Consupedia"
                  className="h-6 w-auto"
                />
              </div>
            </div>
            <div className="mt-8 text-center text-sm">
              <a
                href="mailto:info@consupedia.com"
                className="group  text-primary-dark hover:bg-gray-50 px-10 mobile:px-12 py-5 mobile:py-6 rounded-2xl text-sm mobile:text-base font-bold transition-all duration-300 hover:shadow-3xl hover:scale-105 transform inline-flex items-center"
              >
                <span className="flex items-center">
                  Kontakta Oss
                  <svg
                    className="w-6 h-6 ml-3 group-hover:translate-x-2 transition-transform duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LandingPage;
